import { useField } from "formik";
import React from "react";
import { Trans, useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import Paths from "routes/Paths";

import styles from "./TermsAcceptance.module.css";

/** Formik field this control is bound to; also the id the label and error message point at. */
const FIELD_NAME = "acceptedTerms";

/**
 * The explicit agreement that turns registration into a recorded acceptance rather than a notice
 * nobody read. It starts unticked on purpose: a pre-ticked box is not consent, and the whole point
 * of this control is that it can be produced later as proof the account holder agreed to carry
 * responsibility for what they upload. The API refuses the registration too, so removing this from
 * the DOM buys nothing.
 */
const TermsAcceptance = () => {
	// Claims the namespace itself rather than relying on a parent to have loaded it, and hands the
	// resulting `t` to Trans so the sentence cannot render as a raw key on a cold cache.
	const { t } = useTranslation("accountsPage");
	// `type: "checkbox"` makes Formik populate `checked`; its `value` is then the same boolean, which
	// an <input> will not take, so it is dropped rather than spread.
	const [{ value, ...checkbox }, meta] = useField<boolean>({ name: FIELD_NAME, type: "checkbox" });
	const hasError = Boolean(meta.touched && meta.error);

	return (
		<div className={styles.wrapper}>
			<label className={styles.control} htmlFor={FIELD_NAME}>
				<input
					aria-describedby={hasError ? `${FIELD_NAME}-error` : undefined}
					aria-invalid={hasError}
					className={styles.checkbox}
					id={FIELD_NAME}
					type="checkbox"
					{...checkbox}
				/>
				{/* The policy links sit inside the label so the sentence reads as one thing. Clicking
				    one does not toggle the box — a label's activation behaviour skips clicks aimed at
				    interactive descendants — and they open in a new tab, so someone who stops to read
				    the Terms comes back to a form they have not had to fill in twice. */}
				<span className={styles.text}>
					<Trans
						components={{
							privacy: <Link rel="noopener noreferrer" target="_blank" to={Paths.privacyPolicy} />,
							terms: <Link rel="noopener noreferrer" target="_blank" to={Paths.termsOfService} />
						}}
						i18nKey="termsAcceptance.label"
						t={t}
					/>
				</span>
			</label>
			<span className={styles.message} id={`${FIELD_NAME}-error`}>
				{hasError ? meta.error : ""}
			</span>
		</div>
	);
};

export default TermsAcceptance;
